import React from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import SectionHeader from './SectionHeader';
import ColumnVisibilityDropdown from './ColumnVisibilityDropdown';
import TableContainer from './TableContainer';
import EmptyState from './EmptyState';
import { STYLES } from './types';

interface ProductsViewProps {
  transactionId: string;
  products: any[];
  loading: boolean;
  error: any;
  columns: any[];
  columnOptions: Array<{id: string; label: string; visible: boolean}>;
  onToggleColumn: (id: string, visible: boolean) => void;
}

/**
 * Component for displaying products in a transaction
 */
const ProductsView: React.FC<ProductsViewProps> = ({
  transactionId,
  products,
  loading,
  error,
  columns,
  columnOptions,
  onToggleColumn
}) => {
  const { t } = useTranslation();
  // Set up the table
  const productsTable = useAdvanceTable({
    data: products,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });
  
  // Calculate total items (sum of all product quantities)
  const totalItems = products.reduce((sum, product) => sum + (product.qty || 0), 0);

  return (
    <>
      <SectionHeader title={t('transactions.detail.totalProducts', 'Total Products: {{count}} | Total Items: {{items}}', { count: products.length, items: totalItems })}>
        <ColumnVisibilityDropdown 
          options={columnOptions} 
          onToggle={onToggleColumn}
        />
      </SectionHeader>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>{t('transactions.detail.errorHeading', 'Error Loading Product Details')}</Alert.Heading>
          <p>
            {t('transactions.detail.errorText', 'There was a problem loading the details for this transaction. Please try again later or contact support if the problem persists.')}
          </p>
        </Alert>
      )}
      
      <AdvanceTableProvider {...productsTable}>
        <TableContainer loading={loading}>
          {!loading && !error && products.length === 0 ? (
            <EmptyState message={t('transactions.detail.noProductsText', 'No products found for this transaction')} />
          ) : (
            <>
              <AdvanceTable tableProps={STYLES.table.props} />
              <AdvanceTableFooter 
                pagination 
                totalCount={products.length} 
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </TableContainer>
      </AdvanceTableProvider>
    </>
  );
};

export default React.memo(ProductsView);
